/**
 * 郵便番号・住所の相互変換（外部API）を扱うヘルパー。
 * - 郵便番号 → 住所: zipcloud.ibsnet.co.jp（カナ未取得時は teraren.com で補完）
 * - 住所 → 郵便番号: nominatim.openstreetmap.org
 */

const JP_PREFECTURES = [
    '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
    '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
    '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
    '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
    '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
    '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
    '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県',
].sort((a, b) => b.length - a.length);

export const extractLeadingPrefecture = (addr) => {
    const n = String(addr || '').normalize('NFKC').trim();
    for (const p of JP_PREFECTURES) {
        if (n.startsWith(p)) return p;
    }
    return null;
};

export const normalizePrefectureName = (raw) => {
    const s = String(raw || '').normalize('NFKC').trim();
    if (!s) return '';
    if (/(都|道|府|県)$/.test(s)) return s;
    if (s === '北海道') return '北海道';
    if (s === '東京') return '東京都';
    if (s === '大阪') return '大阪府';
    if (s === '京都') return '京都府';
    return `${s}県`;
};

/** "1234567" / "123-4567" 等を "123-4567" 形式に整形 */
export const formatZipcode = (value) => {
    let v = String(value || '').replace(/\D/g, '');
    if (v.length > 7) v = v.slice(0, 7);
    if (v.length >= 4) return v.slice(0, 3) + '-' + v.slice(3);
    return v;
};

const fetchKanaFromTeraren = async (digits) => {
    try {
        const res = await fetch(`https://postcode.teraren.com/postcodes/${digits}.json`);
        if (!res.ok) return '';
        const data = await res.json();
        return [data.prefecture_kana, data.city_kana, data.suburb_kana]
            .filter(Boolean)
            .join('');
    } catch {
        return '';
    }
};

/**
 * 郵便番号(7桁数字 or "123-4567")から住所文字列を返す。
 * @returns {Promise<{addressKanji: string, addressKana: string} | null>}
 */
export const fetchAddressFromZipcode = async (zipcode) => {
    const digits = String(zipcode || '').replace(/\D/g, '');
    if (digits.length !== 7) return null;
    try {
        const res = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${digits}`);
        const json = await res.json();
        if (json.status === 200 && json.results && json.results[0]) {
            const r = json.results[0];
            const addressKanji = [r.address1, r.address2, r.address3].filter(Boolean).join('');
            let addressKana = [r.kana1, r.kana2, r.kana3].filter(Boolean).join('');
            if (!addressKana) {
                addressKana = await fetchKanaFromTeraren(digits);
            }
            return { addressKanji, addressKana };
        }
    } catch (err) {
        console.warn('郵便番号検索エラー:', err);
    }
    return null;
};

/**
 * 末尾の番地・号・建物名を取り除いた住所を返す。
 * 例：「東京都新宿区西新宿2-8-1 都庁第一本庁舎」→「東京都新宿区西新宿」
 */
const stripStreetNumber = (addr) => {
    let cur = String(addr || '')
        .replace(/[\s　].*$/, '') // 空白以降（建物名等）を削除
        .replace(/[0-9０-９\-‐ー－―]+$/u, '') // 末尾の数字・ハイフン
        .replace(/[0-9０-９]+号$/u, '')
        .replace(/[0-9０-９]+番地?$/u, '')
        .replace(/丁目$/u, '')
        .trim();
    // 念のためもう1〜2回繰り返して短縮する
    for (let i = 0; i < 4; i++) {
        const next = cur
            .replace(/[0-9０-９]+番地?$/u, '')
            .replace(/[0-9０-９]+号$/u, '')
            .replace(/[0-9０-９\-‐ー－―]+$/u, '')
            .replace(/丁目$/u, '')
            .trim();
        if (!next || next === cur) break;
        cur = next;
    }
    return cur;
};

/**
 * 入力された住所文字列を、検索に使いやすい「キーワード」群に整形する。
 *  - 番地・建物名を取り除いたもののみを採用（API 404 を避けるため）
 *  - 段階的に短くした候補も用意
 */
const buildAddressQueryVariants = (raw) => {
    const addr = String(raw || '').normalize('NFKC').trim().replace(/\s+/g, ' ');
    if (!addr) return [];
    const variants = [];
    const seen = new Set();
    const push = (v) => {
        const t = (v || '').trim();
        if (!t || t.length < 2 || seen.has(t)) return;
        seen.add(t);
        variants.push(t);
    };

    // 番地・建物名を取り除いた基本クエリ
    const base = stripStreetNumber(addr);
    push(base);

    // さらに末尾の町名を1段階短くしたバリエーション（市区町村単位）
    if (base) {
        const noSuburb = base.replace(/[一-龯ぁ-んァ-ヶー]+$/u, '').trim();
        if (noSuburb && noSuburb.length >= 4) push(noSuburb);
    }

    // 元の住所に数字やハイフンが含まれていない場合のみ、原文も最後の候補として追加
    if (!/[0-9０-９\-‐ー－―]/.test(addr)) {
        push(addr);
    }

    return variants;
};

const fetchTerarenCandidates = async (keyword) => {
    try {
        const url = `https://postcode.teraren.com/postcodes.json?s=${encodeURIComponent(keyword)}&per=20`;
        // 404 など見つからない場合の挙動はブラウザコンソールに出るが、JS 側は静かに無視する
        const res = await fetch(url).catch(() => null);
        if (!res || !res.ok) return [];
        const data = await res.json().catch(() => null);
        if (!Array.isArray(data)) return [];
        return data
            .map((r) => {
                // teraren の住所検索 API は `new`（7桁の新郵便番号）を返す
                const digits = String(r.new || r.postcode || r.new_postcode || '').replace(/\D/g, '');
                if (digits.length !== 7) return null;
                const label = [r.prefecture, r.city, r.suburb].filter(Boolean).join('');
                return { zip: digits, label };
            })
            .filter(Boolean);
    } catch (_e) {
        return [];
    }
};

const fetchNominatimCandidates = async (keyword, pref) => {
    try {
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(keyword)}&format=json&addressdetails=1&countrycodes=jp&limit=10&accept-language=ja`;
        const res = await fetch(url).catch(() => null);
        if (!res || !res.ok) return [];
        const results = await res.json().catch(() => null);
        if (!Array.isArray(results) || results.length === 0) return [];
        const out = [];
        for (const item of results) {
            const postcode = item.address?.postcode;
            if (!postcode) continue;
            const digits = postcode.replace(/\D/g, '');
            if (digits.length !== 7) continue;
            if (pref) {
                const itemPref = normalizePrefectureName(item.address?.province || '');
                if (itemPref && itemPref !== pref) continue;
            }
            const displayPref = item.address?.province || '';
            const displayCity = item.address?.city || item.address?.town || item.address?.county || '';
            const displaySub = item.address?.neighbourhood || item.address?.suburb || item.address?.quarter || '';
            out.push({ zip: digits, label: `${displayPref}${displayCity}${displaySub}` });
        }
        return out;
    } catch (_e) {
        return [];
    }
};

/**
 * 住所文字列から候補となる郵便番号配列を返す。
 *
 * 1) postcode.teraren.com（住所キーワード検索）を優先
 *    末尾の番地・建物名を削った段階的なクエリで再試行
 * 2) 失敗時のフォールバックとして nominatim も検索
 *
 * @returns {Promise<Array<{zip: string, label: string}>>}
 */
export const lookupZipCandidates = async (addrRaw) => {
    const addr = String(addrRaw || '').normalize('NFKC').trim();
    if (addr.length < 2) return [];

    const pref = extractLeadingPrefecture(addr);
    const queries = buildAddressQueryVariants(addr);

    const seen = new Set();
    const merged = [];

    // 1) teraren を段階的キーワードで試す
    for (const q of queries) {
        const found = await fetchTerarenCandidates(q);
        for (const c of found) {
            if (seen.has(c.zip)) continue;
            // 都道府県の指定があれば、ラベル先頭で都道府県一致を緩めに確認
            if (pref && c.label && !c.label.startsWith(pref)) continue;
            seen.add(c.zip);
            merged.push(c);
            if (merged.length >= 20) break;
        }
        if (merged.length > 0) break; // 1つでも見つかれば打ち切り
    }

    if (merged.length > 0) return merged;

    // 2) フォールバック：nominatim
    for (const q of queries) {
        const found = await fetchNominatimCandidates(q, pref);
        for (const c of found) {
            if (seen.has(c.zip)) continue;
            seen.add(c.zip);
            merged.push(c);
        }
        if (merged.length > 0) break;
    }
    return merged;
};
